// Traffic-aware travel — a pure, documented, CONSERVATIVE estimate.
//
// The free OSM stack (protocol-day-setup) gives a free-flow OSRM driving time
// with no live traffic. This module scales that base by a time-of-day factor for
// the hour the driver LEAVES home, so a 07:00 route (leaving into rush hour)
// wakes the driver a little earlier than the same drive at 22:00 would.
//
// Invariants (mirrored in supabase/functions/_shared/auto-day-spec.ts —
// trafficAdjustedTravel — and covered by the JS↔TS identity test):
//   • Factors are ALL ≥ 1.0: we never promise a faster drive than the map.
//   • A 'manual' travel time is returned untouched — the driver's own number is
//     never inflated.
//   • Result is capped at 240 minutes, and within that cap never below the
//     base estimate. (A base itself above 240 is returned as 240 — the cap
//     wins over the never-below-base rule in that extreme.)
// It is an estimate and is labelled as one in the UI.

import { TRAFFIC_FACTORS } from '@/lib/autoDaySpec';

// Local hour (0–23) → multiplier. Out-of-range hours fall back to 1.0.
export function trafficFactor(hour) {
  const h = Number(hour);
  if (!Number.isInteger(h) || h < 0 || h > 23) return 1.0;
  return TRAFFIC_FACTORS[h] ?? 1.0;
}

// Effective travel minutes for a route morning.
//   baseTravel         free-flow minutes (settings.travel_minutes)
//   routeStartMinutes  the day's route start (minutes since midnight)
//   travelSource       'auto' → scale; 'manual' → return base unchanged
export function trafficAdjustedTravel(baseTravel, routeStartMinutes, travelSource = 'manual') {
  const base = Number(baseTravel);
  if (!Number.isFinite(base)) return base;
  if (travelSource === 'manual') return Math.round(base);
  const start = Number(routeStartMinutes);
  if (!Number.isFinite(start)) return Math.round(base);
  // Approximate the leave minute (routeStart − 15 − base) to pick the hour, then
  // scale once (the factor table is coarse — hourly — so one pass is enough).
  const approxLeave = start - 15 - base;
  const hour = Math.floor((((Math.round(approxLeave) % 1440) + 1440) % 1440) / 60);
  const eff = Math.round(base * trafficFactor(hour));
  return Math.min(240, Math.max(Math.round(base), eff));
}
